import { Component } from '@angular/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionsService } from '../../features/permissions/permissions.service';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import * as PermissionActions from '../../core/permissions/permission.action';

@Component({
  selector: 'app-permissions',
  standalone: true,
  imports: [MatSlideToggleModule, MatExpansionModule, CommonModule],
  templateUrl: './permissions.component.html',
  styleUrl: './permissions.component.scss',
})
export class PermissionsComponent {
  assignedPermissionIds: Set<number> = new Set();
  selectedPermissionIds: Set<number> = new Set();
  permissions: any[] = [];
  permissionId: any = null;
  selectedPermissionKeys: string[] = [];
  uncheckedPermissionKeys: string[] = [];

  constructor(
    private permissionsService: PermissionsService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.permissionId = params.get('id');

      if (this.permissionId) {
        this.loadPermissionsAndAssignments(this.permissionId);
      }
    });
  }

  loadPermissionsAndAssignments(id: string): void {
    this.permissionsService.getPermissions().subscribe({
      next: (res) => {
        this.permissions = res.permissions;
        this.permissionsService.getPermissionsById(id).subscribe({
          next: (res2) => {
            const assignedIds = new Set<number>(
              (res2.data?.[0]?.linearPermissions || []) as number[]
            );
            this.assignedPermissionIds = assignedIds;
            this.selectedPermissionIds = assignedIds;
            this.markAssignedPermissions();
          },
          error: (err) => {
            console.error('Error fetching assigned permissions:', err);
          },
        });
      },
      error: (err) => {
        console.error('Error fetching permissions:', err);
      },
    });
  }

  markAssignedPermissions(): void {
    for (const module of this.permissions) {
      let moduleChecked = false;
      // Module-level permissions
      for (const perm of module.linearPermission) {
        perm.checked = this.assignedPermissionIds.has(perm.id);
        if (perm.checked) moduleChecked = true;
      }
      // Submodules
      for (const sub of module.subModule) {
        let subChecked = false;
        for (const perm of sub.linearPermission) {
          perm.checked = this.assignedPermissionIds.has(perm.id);
          if (perm.checked) subChecked = true;
        }
        sub.checked = subChecked;
        if (subChecked) moduleChecked = true;
      }
      module.checked = moduleChecked;
    }
  }

  findPermissionKeyById(id: number): string | null {
    for (const module of this.permissions) {
      for (const perm of module.linearPermission || []) {
        if (perm.id === id) return perm.key;
      }
      for (const sub of module.subModule || []) {
        for (const perm of sub.linearPermission || []) {
          if (perm.id === id) return perm.key;
        }
      }
    }
    return null;
  }

  extractPermissionType(permissionName: string): string {
    return permissionName.split(' ')[0].toLowerCase();
  }

  findViewPermission(permId: number): any {
    for (const module of this.permissions) {
      for (const perm of module.linearPermission || []) {
        if (perm.id === permId) {
          return module.linearPermission.find((p: any) =>
            this.extractPermissionType(p.name) === 'view'
          );
        }
      }

      for (const sub of module.subModule || []) {
        for (const perm of sub.linearPermission || []) {
          if (perm.id === permId) {
            return sub.linearPermission.find((p: any) =>
              this.extractPermissionType(p.name) === 'view'
            );
          }
        }
      }
    }
    return null;
  }

  findAllPermissionsInSameContext(permId: number): any[] {
    for (const module of this.permissions) {
      for (const perm of module.linearPermission || []) {
        if (perm.id === permId) {
          return module.linearPermission || [];
        }
      }

      for (const sub of module.subModule || []) {
        for (const perm of sub.linearPermission || []) {
          if (perm.id === permId) {
            return sub.linearPermission || [];
          }
        }
      }
    }
    return [];
  }

  togglePermission(permId: number, checked: boolean): void {
    const permissionKey = this.findPermissionKeyById(permId);
    const permission = this.findPermissionById(permId);

    if (!permission) return;

    const permissionType = this.extractPermissionType(permission.name);

    if (permissionKey) {
      if (checked) {
        this.selectedPermissionIds.add(permId);
        if (!this.selectedPermissionKeys.includes(permissionKey)) {
          this.selectedPermissionKeys.push(permissionKey);
        }
        const index = this.uncheckedPermissionKeys.indexOf(permissionKey);
        if (index > -1) this.uncheckedPermissionKeys.splice(index, 1);

        if (permissionType !== 'view') {
          this.autoCheckViewPermission(permId);
        }
      } else {
        if (permissionType === 'view') {
          this.uncheckAllPermissionsInContext(permId);
        } else {
          this.selectedPermissionIds.delete(permId);
          const index = this.selectedPermissionKeys.indexOf(permissionKey);
          if (index > -1) this.selectedPermissionKeys.splice(index, 1);
          if (!this.uncheckedPermissionKeys.includes(permissionKey)) {
            this.uncheckedPermissionKeys.push(permissionKey);
          }
        }
      }
    }

    this.updateAllPermissionStates();
    this.updateModuleKeyStates();
  }

  autoCheckViewPermission(permId: number): void {
    const viewPermission = this.findViewPermission(permId);
    if (viewPermission && !this.selectedPermissionIds.has(viewPermission.id)) {
      this.selectedPermissionIds.add(viewPermission.id);
            const viewPermissionKey = this.findPermissionKeyById(viewPermission.id);
      if (viewPermissionKey && !this.selectedPermissionKeys.includes(viewPermissionKey)) {
        this.selectedPermissionKeys.push(viewPermissionKey);
      }
    }
  }

  uncheckAllPermissionsInContext(viewPermId: number): void {
    const allPermissionsInContext = this.findAllPermissionsInSameContext(viewPermId);

    for (const perm of allPermissionsInContext) {
      this.selectedPermissionIds.delete(perm.id);
            const permissionKey = this.findPermissionKeyById(perm.id);
      if (permissionKey) {
        const index = this.selectedPermissionKeys.indexOf(permissionKey);
        if (index > -1) this.selectedPermissionKeys.splice(index, 1);
        if (!this.uncheckedPermissionKeys.includes(permissionKey)) {
          this.uncheckedPermissionKeys.push(permissionKey);
        }
      }
    }
  }

  findPermissionById(permId: number): any {
    for (const module of this.permissions) {
      for (const perm of module.linearPermission || []) {
        if (perm.id === permId) return perm;
      }
      for (const sub of module.subModule || []) {
        for (const perm of sub.linearPermission || []) {
          if (perm.id === permId) return perm;
        }
      }
    }
    return null;
  }

  updateAllPermissionStates(): void {
    for (const module of this.permissions) {
      for (const perm of module.linearPermission || []) {
        perm.checked = this.selectedPermissionIds.has(perm.id);
      }
      for (const sub of module.subModule || []) {
        for (const perm of sub.linearPermission || []) {
          perm.checked = this.selectedPermissionIds.has(perm.id);
        }
      }
    }
  }

  updateModuleKeyStates(): void {
    for (const module of this.permissions) {
      const allPerms = [...(module.linearPermission || [])];
      if (allPerms.length === 0) continue;
      const allChecked = allPerms.every((p) =>
        this.selectedPermissionIds.has(p.id)
      );
      const noneChecked = allPerms.every(
        (p) => !this.selectedPermissionIds.has(p.id)
      );
      const modKey = module.key;
      if (allChecked) {
        if (!this.selectedPermissionKeys.includes(modKey)) {
          this.selectedPermissionKeys.push(modKey);
        }
        const idx = this.uncheckedPermissionKeys.indexOf(modKey);
        if (idx > -1) this.uncheckedPermissionKeys.splice(idx, 1);
      } else if (noneChecked) {
        if (!this.uncheckedPermissionKeys.includes(modKey)) {
          this.uncheckedPermissionKeys.push(modKey);
        }
        const idx = this.selectedPermissionKeys.indexOf(modKey);
        if (idx > -1) this.selectedPermissionKeys.splice(idx, 1);
      } else {
        this.selectedPermissionKeys = this.selectedPermissionKeys.filter(
          (k) => k !== modKey
        );
        this.uncheckedPermissionKeys = this.uncheckedPermissionKeys.filter(
          (k) => k !== modKey
        );
      }
    }
  }

  toggleModulePermissions(module: any, checked: boolean): void {
    module.checked = checked;
    for (const perm of module.linearPermission || []) {
      perm.checked = checked;
      this.togglePermission(perm.id, checked);
    }
    for (const sub of module.subModule || []) {
      sub.checked = checked;
      for (const perm of sub.linearPermission || []) {
        perm.checked = checked;
        this.togglePermission(perm.id, checked);
      }
    }
    this.updateModuleKeyStates();
  }

  toggleSubModulePermissions(sub: any, checked: boolean): void {
    sub.checked = checked;

    for (const perm of sub.linearPermission || []) {
      perm.checked = checked;
      this.togglePermission(perm.id, checked);
    }

    this.updateModuleKeyStates();
  }

  applyPermissions(): void {
    const payload = {
      roleId: this.permissionId,
      permissionIds: Array.from(this.selectedPermissionIds),
    };
    this.permissionsService
      .updatePermission(this.permissionId, payload)
      .subscribe({
        next: () => {
          this.toastr.success('Permissions updated successfully');
          this.router.navigate(['/roles']);
          const roleID = sessionStorage.getItem('roleID');
          if (roleID && roleID === this.permissionId) {
            this.selectedPermissionKeys.forEach((key) => {
              this.store.dispatch(
                PermissionActions.addPermission({ permission: key })
              );
            });
            this.uncheckedPermissionKeys.forEach((key) => {
              this.store.dispatch(
                PermissionActions.removePermission({ permission: key })
              );
            });
          }
          this.loadPermissionsAndAssignments(this.permissionId);
        },
        error: (err) => {
          console.error('Failed to update permissions:', err);
          this.toastr.error('Failed to update permissions');
        },
      });
  }
}
